import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarCheck, Info, Star } from 'lucide-react';
import { ProfessionalCard } from '@/components/session/components/ProfessionalCard';
import { buildMatchSchedulingPath } from '@/utils/matchHandoff';
import { formatAnsweredAt } from '../matchFlow';

interface MatchResultsProps {
	result: Match.Result;
	/** true quando o usuário já tinha respondido antes (mostra o aviso). */
	alreadyAnswered: boolean;
}

const CARD_ENTER = {
	hidden: { opacity: 0, y: 32, scale: 0.96 },
	show: (i: number) => ({
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			delay: 0.15 + i * 0.16,
			type: 'spring' as const,
			stiffness: 160,
			damping: 18,
		},
	}),
};

/** Os 3 terapeutas do match. Escolher um leva ao agendamento padrão (passo de horário). */
const MatchResults = ({ result, alreadyAnswered }: MatchResultsProps) => {
	const navigate = useNavigate();
	const professionals = [...result.professionals].sort(
		(a, b) => a.rank - b.rank,
	);

	const handleSelect = (
		professionalId: string,
		slot?: { start_time: string },
	) => {
		navigate(buildMatchSchedulingPath(professionalId, slot?.start_time));
	};

	return (
		<div className="flex w-full flex-col gap-8">
			{alreadyAnswered ? (
				<motion.div
					initial={{ opacity: 0, y: -8 }}
					animate={{ opacity: 1, y: 0 }}
					className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
				>
					<Info className="mt-0.5 h-4 w-4 shrink-0" />
					<p className="text-sm font-medium">
						Você já respondeu ao match nesta conta em{' '}
						<strong>{formatAnsweredAt(result.answered_at)}</strong>.
						O questionário só pode ser respondido uma vez, então
						aqui está o seu resultado.
					</p>
				</motion.div>
			) : null}

			<div className="space-y-3">
				<motion.p
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-brand-pink"
				>
					<Star className="h-3.5 w-3.5 fill-current" />
					Seu match
				</motion.p>
				<motion.h1
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.05 }}
					className="text-2xl font-black leading-tight tracking-tight text-stone-900 md:text-[2rem]"
				>
					{alreadyAnswered
						? 'Estes são os terapeutas indicados para você.'
						: 'Encontramos 3 terapeutas com afinidade com você.'}
				</motion.h1>
				<motion.p
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}
					className="max-w-xl text-base font-medium text-stone-500"
				>
					Na ordem de afinidade. Conheça cada um e escolha com quem
					quer começar: você vai direto para a escolha de horário.
				</motion.p>
			</div>

			{result.fallback ? (
				<motion.div
					initial={{ opacity: 0, y: -8 }}
					animate={{ opacity: 1, y: 0 }}
					className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 text-stone-700"
				>
					<Info className="mt-0.5 h-4 w-4 shrink-0" />
					<p className="text-sm font-medium">
						{result.fallback_reason === 'LOW_CONFIDENCE'
							? 'Suas respostas deixaram quase tudo em aberto, então mostramos os terapeutas mais indicados da Plumaa. Qualquer um deles é um bom começo.'
							: 'Ainda estamos completando o perfil dos terapeutas, então mostramos os mais indicados da Plumaa. Qualquer um deles é um bom começo.'}
					</p>
				</motion.div>
			) : null}

			<div className="flex flex-col gap-5 pt-3">
				{professionals.map((professional, index) => (
					<motion.div
						key={professional.id}
						custom={index}
						variants={CARD_ENTER}
						initial="hidden"
						animate="show"
						className="flex flex-col gap-2"
					>
						{professional.match_score != null ||
						(professional.match_reasons?.length ?? 0) > 0 ? (
							<div className="flex flex-wrap items-center gap-2 px-1">
								{professional.match_score != null ? (
									<span
										className="inline-flex items-center gap-1.5 rounded-full bg-brand-pink/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-brand-pink"
										title="Afinidade com suas respostas"
									>
										<Star className="h-3 w-3 fill-current" />
										{professional.match_score}% de afinidade
									</span>
								) : null}
								{(professional.match_reasons ?? []).map(
									(reason) => (
										<span
											key={reason}
											className="inline-flex items-center rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-semibold text-stone-600"
										>
											{reason}
										</span>
									),
								)}
							</div>
						) : null}
						<ProfessionalCard
							professional_id={professional.id}
							name_prefix={professional.name_prefix}
							first_name={professional.first_name}
							last_name={professional.last_name}
							profile_photo_url={professional.profile_photo_url}
							video_url={professional.video_url}
							crm_or_crp={professional.crm_or_crp}
							brief_description={professional.brief_description}
							service_name={
								professional.service_name ?? undefined
							}
							highlight_tag={professional.highlight_tag}
							main_approach={professional.main_approach}
							secondary_approaches={
								professional.secondary_approaches
							}
							focus_topics={professional.focus_topics}
							matchRank={professional.rank}
							isSelected={false}
							selectedButtonText="Agendar"
							onClick={() => handleSelect(professional.id)}
							onScheduleSlot={(slot) =>
								handleSelect(professional.id, slot)
							}
						/>
					</motion.div>
				))}
			</div>

			{professionals.length === 0 ? (
				<p className="rounded-2xl border border-dashed border-stone-200 bg-white py-8 text-center text-sm text-stone-400">
					Ainda não há profissionais disponíveis para o match. Tente
					novamente em breve.
				</p>
			) : null}

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.7 }}
				className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 pt-6"
			>
				<p className="text-xs font-medium text-stone-400">
					Os profissionais do seu match aparecem com uma estrela
					também na lista completa.
				</p>
				<button
					type="button"
					onClick={() => navigate('/agendamento')}
					className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900"
				>
					<CalendarCheck className="h-4 w-4" />
					Ver todos os profissionais
				</button>
			</motion.div>
		</div>
	);
};

export { MatchResults };
